import React, { type FC, useState } from 'react'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'
import { Typography } from '../ui/Typography'
import { ButtonRegular } from '../ui/Button'
import { colors } from '../styles/colors'
import { CHAIN } from '@tonconnect/protocol'
import { shortenWalletAddress } from '../functions/shortenWalletAddress'
import WithdrawalTopology from '../svgr/WithdrawalTopology'
import type { Wallet } from '@tonconnect/ui-react'
import { DialogContainer } from './common.styles'
import {
  trackBonusWithdrawalSuccess,
  trackBonusWithdrawalFailed,
  extractErrorMessage,
} from '../utils/amplitude'

export interface WithdrawalConfirmDialogProps {
  onConfirm: () => Promise<void>
  amount: string
  wallet: Wallet
  isLoading?: boolean
}

export const WithdrawalConfirmDialog: FC<WithdrawalConfirmDialogProps> = ({
  onConfirm,
  amount,
  wallet,
  isLoading = false,
}) => {
  const [isConfirming, setIsConfirming] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      await onConfirm()
      trackBonusWithdrawalSuccess(amount)
      setIsSuccess(true)
    } catch (error) {
      console.error('Withdrawal confirmation error:', error)
      trackBonusWithdrawalFailed(extractErrorMessage(error))
    } finally {
      setIsConfirming(false)
    }
  }

  const currentLoading = isLoading || isConfirming
  const isTestnet = wallet.account.chain === CHAIN.TESTNET
  const walletAddress = shortenWalletAddress(wallet.account.address, isTestnet)

  // Success state
  if (isSuccess) {
    return (
      <DialogContainer>
        <IconContainer>
          <WithdrawalTopology />
        </IconContainer>

        <ContentContainer>
          <TitleContainer>
            <Typography
              fontSize={16}
              fontWeight={400}
              color={colors.textSecondary}
            >
              <FormattedMessage
                id="dialog.withdrawal.processing"
                defaultMessage="Транзакция выполняется..."
              />
            </Typography>
          </TitleContainer>
        </ContentContainer>
      </DialogContainer>
    )
  }

  // Confirm state
  return (
    <DialogContainer>
      <IconContainer>
        <WithdrawalTopology />
      </IconContainer>

      <ContentContainer>
        <TitleContainer>
          <Typography fontSize={20} fontWeight={600} color={colors.secondary}>
            <FormattedMessage
              id="dialog.withdrawal.title"
              defaultMessage="Подтвердите вывод"
            />
          </Typography>
          <Typography
            fontSize={14}
            fontWeight={400}
            color={colors.textSecondary}
          >
            <FormattedMessage
              id="dialog.withdrawal.subtitle"
              defaultMessage="Проверьте реквезиты и подтвердите вывод"
            />
          </Typography>
        </TitleContainer>

        <DetailsContainer>
          <DetailsItem>
            <dt>
              <Typography fontSize={14} fontWeight={400} color={colors.grey700}>
                <FormattedMessage
                  id="dialog.withdrawal.toWithdraw"
                  defaultMessage="К выводу"
                />
              </Typography>
            </dt>
            <dd>
              <Typography
                fontSize={14}
                fontWeight={400}
                color={colors.grey700}
                font="roboto"
              >
                {amount} TON
              </Typography>
            </dd>
          </DetailsItem>

          <DetailsItem>
            <dt>
              <Typography fontSize={14} fontWeight={400} color={colors.grey700}>
                <FormattedMessage
                  id="dialog.withdrawal.wallet"
                  defaultMessage="Кошелек"
                />
              </Typography>
            </dt>
            <dd>
              <Typography
                fontSize={14}
                fontWeight={600}
                color={colors.grey700}
                font="roboto"
              >
                {walletAddress}
              </Typography>
            </dd>
          </DetailsItem>
        </DetailsContainer>
      </ContentContainer>

      <ButtonsContainer>
        <ConfirmButton
          loading={currentLoading}
          onClick={handleConfirm}
          disabled={currentLoading}
          textColor={colors.white}
          textWeight={700}
        >
          <FormattedMessage
            id="dialog.withdrawal.confirmButton"
            defaultMessage="Вывести"
          />
        </ConfirmButton>
      </ButtonsContainer>
    </DialogContainer>
  )
}

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ContentContainer = styled.div`
  display: grid;
  gap: 24px;
  width: 100%;
`

const TitleContainer = styled.div`
  text-align: center;
  display: grid;
  gap: 8px;
`

const DetailsContainer = styled.dl`
  display: grid;
  gap: 16px;
  margin: 0;
  width: 100%;
`

const DetailsItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;

  dt,
  dd {
    margin: 0;
  }

  dt {
    text-align: left;
  }

  dd {
    text-align: right;
  }
`

const ButtonsContainer = styled.div`
  width: 100%;
  display: grid;
  gap: 12px;
`

const ConfirmButton = styled(ButtonRegular)`
  width: 100%;
  background: ${colors.secondary};
  color: ${colors.white};
  border-radius: ${colors.radiusPill};
`
